import { Divider, type FormItemProps, Select as AntdSelect, type SelectProps, Space, Table } from 'antd';
import { useField } from 'formik';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type DealerListDataFragment } from '../../../api/fragments/DealerListData';
import { type UserGroupSpecsFragment } from '../../../api/fragments/UserGroupSpecs';
import { type ListDealersQuery, useListDealersQuery } from '../../../api/queries/listDealers';
import { Select } from '../../../components/fields/SelectField';
import TableField from '../../../components/fields/TableField';

type Record = ListDealersQuery['page']['items'][number];

export type SelectDealersWithSearchFieldProps = Omit<SelectProps, 'options' | 'children'> & {
    name: string;
    dealers?: DealerListDataFragment[];
    displayName?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    userGroupId?: string;
    userGroup?: UserGroupSpecsFragment;
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.children.toString().toLowerCase().includes(inputValue.toLowerCase());

const SelectDealersWithSearchField = ({
    dealers: dealersFromProps,
    name,
    displayName,
    itemProps,
    userGroupId,
    userGroup,
    ...props
}: SelectDealersWithSearchFieldProps) => {
    const { t } = useTranslation(['userGroupDetails']);

    const { data, loading } = useListDealersQuery({
        fetchPolicy: 'cache-and-network',
        skip: !!dealersFromProps,
    });

    const [{ value: selectedDealerIds }, , { setValue }] = useField({ name });
    const dealers = useMemo(() => dealersFromProps || data?.page.items || [], [data, dealersFromProps]);

    const dealerOptions = useMemo(
        () =>
            dealers.filter(
                dealer =>
                    !selectedDealerIds.includes(dealer.id) &&
                    !userGroup.dealerIds.includes(dealer.id) &&
                    dealer.company.id === userGroup.company.id
            ),
        [dealers, selectedDealerIds, userGroup]
    );

    const selectedDealers = useMemo(
        () => dealers.filter(dealer => selectedDealerIds.includes(dealer.id)),
        [dealers, selectedDealerIds]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Select
                filterOption={filterOption}
                onChange={newValue => setValue(newValue)}
                placeholder={t('userGroupDetails:dealerDrawer.placeholder')}
                {...props}
            >
                {dealerOptions.map(dealer => (
                    <AntdSelect.Option key={dealer.id} label={dealer.displayName} value={dealer.id}>
                        {dealer.displayName}
                    </AntdSelect.Option>
                ))}
            </Select>

            <Divider orientation="left" orientationMargin="0" plain>
                {t('userGroupDetails:dealerDrawer.dealersSelected', { returnObjects: true })}
            </Divider>
            <TableField
                dataSource={selectedDealers}
                loading={loading && !selectedDealerIds.length}
                name={name}
                rowKey="id"
                required
            >
                <Table.Column<Record>
                    dataIndex="displayName"
                    onFilter={(value: string, record) => new RegExp(value, 'i').test(record.displayName)}
                    sorter={(a, b) => a.displayName.localeCompare(b.displayName)}
                    title={t('userGroupDetails:dealerDrawer.columns.displayName')}
                    filterSearch
                />
            </TableField>
        </Space>
    );
};

export default memo(SelectDealersWithSearchField);
